import { useCallback } from 'react';
import { useApi, useApiMutation } from './useApi';
import { staysApi } from '../services/staysApi';

/**
 * Hook for fetching spotlight stays
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useSpotlightStays = (options = {}) => {
  return useApi(staysApi.getSpotlightStays, {
    autoFetch: true,
    ...options,
  });
};

/**
 * Hook for searching stays
 * @param {Object} searchParams - Search parameters
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useSearchStays = (searchParams = {}, options = {}) => {
  return useApi(() => staysApi.searchStays(searchParams), {
    autoFetch: false, // Don't auto-fetch, trigger manually
    ...options,
  });
};

/**
 * Hook for fetching all stays
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useAllStays = (options = {}) => {
  return useApi(staysApi.getAllStays, {
    autoFetch: true,
    ...options,
  });
};

/**
 * Hook for searching stays
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useStaysSearch = (options = {}) => {
  return useApi(staysApi.searchStays, {
    autoFetch: false, // Don't auto-fetch, user will trigger search
    ...options,
  });
};

/**
 * Hook for fetching stays by city
 * @param {string} city - City name
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useStaysByCity = (city, options = {}) => {
  // Create stable function reference
  const getStaysByCity = useCallback(async () => {
    return staysApi.getStaysByCity(city);
  }, [city]);

  return useApi(getStaysByCity, {
    autoFetch: !!city, // Only auto-fetch if city is provided
    dependencies: [city],
    ...options,
  });
};

/**
 * Hook for fetching stays by state
 * @param {string} state - State name
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useStaysByState = (state, options = {}) => {
  // Create stable function reference
  const getStaysByState = useCallback(async () => {
    return staysApi.getStaysByState(state);
  }, [state]);

  return useApi(getStaysByState, {
    autoFetch: !!state, // Only auto-fetch if state is provided
    dependencies: [state],
    ...options,
  });
};

/**
 * Hook for fetching high-rated stays
 * @param {number} minRating - Minimum rating threshold
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useHighRatedStays = (minRating = 4.0, options = {}) => {
  // Create stable function reference
  const getHighRatedStays = useCallback(async () => {
    return staysApi.getHighRatedStays(minRating);
  }, [minRating]);

  return useApi(getHighRatedStays, {
    autoFetch: true,
    dependencies: [minRating],
    ...options,
  });
};

/**
 * Hook for fetching a single stay by ID
 * @param {string} stayId - Stay ID
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useStayById = (stayId, options = {}) => {
  // Create stable function reference
  const getStayById = useCallback(async () => {
    return staysApi.getStayById(stayId);
  }, [stayId]);

  return useApi(getStayById, {
    autoFetch: !!stayId, // Only auto-fetch if stayId is provided
    dependencies: [stayId],
    ...options,
  });
};

/**
 * Hook for stays mutations (create, update, delete)
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and functions
 */
export const useStaysMutation = (options = {}) => {
  // This would be used when you add create/update/delete functionality
  // For now, it's a placeholder for future use
  return useApiMutation(async () => {
    throw new Error('Stays mutation not implemented yet');
  }, options);
};

// Export the main hook for backward compatibility
export const useStays = useAllStays; 